import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Snackbar,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    AddToQueue,
    CheckCircle,
    Close,
    Inventory2,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Lock,
    Refresh,
    Replay,
    Search,
} from "@mui/icons-material";
import { getRole } from "../../helper"
import SafeCustodyServices from "../../services/SafeCustodyServices";


/* =========================================================
   Helper Functions
========================================================= */

const getUserRole = () => {
    try {
        const user =
            JSON.parse(localStorage.getItem("user")) ||
            JSON.parse(localStorage.getItem("userData")) ||
            {};

        return String(user?.role || "").toLowerCase();
    } catch (error) {
        return "";
    }
};


const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
};


const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) {
        return "-";
    }

    const totalSeconds = Number(seconds);

    if (Number.isNaN(totalSeconds)) {
        return "-";
    }

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const parts = [];

    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);

    return parts.length ? parts.join(" ") : "< 1m";
};


const getStatusColor = (status) => {
    switch (status) {
        case "active":
            return "success";

        case "pending":
            return "warning";

        case "return_pending":
            return "info";

        case "completed":
            return "default";

        case "none":
            return "default";

        default:
            return "default";
    }
};


/* =========================================================
   Component
========================================================= */

const SafeCustody = () => {
    const role = String(getRole() || "").toLowerCase();

    const isEsimManager = role === "esimmanager";

    const [tab, setTab] = useState(0);

    const [rows, setRows] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    const [search, setSearch] = useState("");

    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [total, setTotal] = useState(0);

    const [loading, setLoading] = useState(false);

    const [reasonDialog, setReasonDialog] = useState(false);
    const [reason, setReason] = useState("");

    const [actionType, setActionType] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });


    /* =========================================================
       Snackbar
    ========================================================= */

    const showMessage = useCallback((message, severity = "success") => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    }, []);


    const closeSnackbar = () => {
        setSnackbar((prev) => ({
            ...prev,
            open: false,
        }));
    };


    /* =========================================================
       Load Factory / Manufacturer / Dealer Data
    ========================================================= */

    const loadPartyData = useCallback(async () => {
        try {
            setLoading(true);
            console.log("Safe Custody Role:", role);
            console.log("Safe Custody Tab:", tab);
            let response;

            if (tab === 0) {
                response =
                    await SafeCustodyServices.safeCustodyEligible({
                        search: search.trim(),
                        page,
                        page_size: pageSize,
                    });
            } else {
                response =
                    await SafeCustodyServices.safeCustodyInCustody({
                        search: search.trim(),
                        page,
                        page_size: pageSize,
                    });
            }

            const data = response?.data || {};

            setRows(data?.results || []);
            setTotal(Number(data?.total || 0));

            setSelectedIds([]);
        } catch (error) {
            console.error(error);

            showMessage(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Failed to load Safe Custody data.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    }, [
        tab,
        search,
        page,
        pageSize,
        showMessage,
    ]);


    /* =========================================================
       Load eSIM Manager Queue
    ========================================================= */

    const loadManagerData = useCallback(async () => {
        try {
            setLoading(true);
            console.log("Safe Custody Role:", role);
            let requestType;
            let status;

            if (tab === 0) {
                requestType = "safe_custody";
                status = "pending";
            } else if (tab === 1) {
                requestType = "return";
                status = "return_pending";
            } else if (tab === 2) {
                requestType = undefined;
                status = undefined;
            } else {
                requestType = undefined;
                status = "completed";
            }

            const payload = {
                search: search.trim(),
                page,
                page_size: pageSize,
            };

            if (requestType) {
                payload.request_type = requestType;
            }

            if (status) {
                payload.status = status;
            }

            const response =
                await SafeCustodyServices.safeCustodyList(
                    payload
                );

            const data = response?.data || {};

            setRows(data?.results || []);
            setTotal(Number(data?.total || 0));

            setSelectedIds([]);
        } catch (error) {
            console.error(error);

            showMessage(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Failed to load Safe Custody requests.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    }, [
        tab,
        search,
        page,
        pageSize,
        showMessage,
    ]);


    /* =========================================================
       Load Data
    ========================================================= */

    useEffect(() => {
        if (isEsimManager) {
            loadManagerData();
        } else {
            loadPartyData();
        }
    }, [
        isEsimManager,
        loadManagerData,
        loadPartyData,
    ]);


    /* =========================================================
       Tab Change
    ========================================================= */

    const handleTabChange = (_, newValue) => {
        if (newValue === tab) return;

        setTab(newValue);
        setPage(1);
        setSearch("");
        setSelectedIds([]);
    };


    /* =========================================================
       Selection
    ========================================================= */

    const getRowId = (row) => {
        if (isEsimManager) {
            return row?.id;
        }

        return row?.id;
    };


    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedIds(
                rows
                    .map((row) => getRowId(row))
                    .filter(Boolean)
            );
        } else {
            setSelectedIds([]);
        }
    };


    const handleSelectRow = (id) => {
        setSelectedIds((previous) => {
            if (previous.includes(id)) {
                return previous.filter(
                    (item) => item !== id
                );
            }

            return [...previous, id];
        });
    };


    const allSelected =
        rows.length > 0 &&
        rows.every((row) =>
            selectedIds.includes(getRowId(row))
        );


    /* =========================================================
       Party Actions
    ========================================================= */

    const openReasonDialog = (type) => {
        if (!selectedIds.length) {
            showMessage(
                "Please select at least one device.",
                "warning"
            );
            return;
        }

        setActionType(type);
        setReason("");
        setReasonDialog(true);
    };


    const closeReasonDialog = () => {
        setReasonDialog(false);
        setReason("");
        setActionType(null);
    };


    const submitPartyAction = async () => {
        try {
            setLoading(true);

            const payload = {
                ids: selectedIds,
            };

            if (reason.trim()) {
                payload.reason = reason.trim();
            }

            let response;

            if (actionType === "safe-custody") {
                response =
                    await SafeCustodyServices.safeCustodyRequest(
                        payload
                    );
            } else {
                response =
                    await SafeCustodyServices.safeCustodyReturnRequest(
                        payload
                    );
            }

            const data = response?.data || {};

            const results = data?.results || [];

            const successCount = results.filter(
                (item) => item?.status === "success"
            ).length;

            const errorCount = results.filter(
                (item) => item?.status === "error"
            ).length;

            if (errorCount > 0) {
                showMessage(
                    `${successCount} successful, ${errorCount} failed.`,
                    errorCount === results.length
                        ? "error"
                        : "warning"
                );
            } else {
                showMessage(
                    `${successCount} device(s) processed successfully.`,
                    "success"
                );
            }

            closeReasonDialog();

            if (isEsimManager) {
                await loadManagerData();
            } else {
                await loadPartyData();
            }
        } catch (error) {
            console.error(error);

            showMessage(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Action failed.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };


    /* =========================================================
       eSIM Manager Actions
    ========================================================= */

    const managerAction = async (type) => {
        if (!selectedIds.length) {
            showMessage(
                "Please select at least one request.",
                "warning"
            );
            return;
        }

        try {
            setLoading(true);

            const payload = {
                ids: selectedIds,
            };

            let response;

            if (type === "approve") {
                response =
                    await SafeCustodyServices.safeCustodyApprove(
                        payload
                    );
            } else {
                response =
                    await SafeCustodyServices.safeCustodyApproveReturn(
                        payload
                    );
            }

            const data = response?.data || {};
            const results = data?.results || [];

            const successCount = results.filter(
                (item) => item?.status === "success"
            ).length;

            const errorCount = results.filter(
                (item) => item?.status === "error"
            ).length;

            if (errorCount > 0) {
                showMessage(
                    `${successCount} successful, ${errorCount} failed.`,
                    errorCount === results.length
                        ? "error"
                        : "warning"
                );
            } else {
                showMessage(
                    `${successCount} request(s) processed successfully.`,
                    "success"
                );
            }

            setSelectedIds([]);

            await loadManagerData();
        } catch (error) {
            console.error(error);

            showMessage(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Approval action failed.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };


    /* =========================================================
       Pagination
    ========================================================= */

    const totalPages = Math.max(
        1,
        Math.ceil(total / pageSize)
    );


    const handlePrevious = () => {
        if (page > 1) {
            setPage((previous) => previous - 1);
        }
    };


    const handleNext = () => {
        if (page < totalPages) {
            setPage((previous) => previous + 1);
        }
    };


    /* =========================================================
       Table Columns
    ========================================================= */

    const partyColumns = useMemo(
        () => [
            {
                key: "id",
                label: "ID",
                render: (row) => row?.id ?? "-",
            },
            {
                key: "uid",
                label: "UID",
                render: (row) => row?.uid || "-",
            },
            {
                key: "imei",
                label: "IMEI",
                render: (row) => row?.imei || "-",
            },
            {
                key: "device_esn",
                label: "Device ESN",
                render: (row) => row?.device_esn || "-",
            },
            {
                key: "stock_upload_status",
                label: "Stock Status",
                render: (row) => (
                    <Chip
                        label={
                            row?.stock_upload_status
                                ? "Uploaded"
                                : "Not Uploaded"
                        }
                        size="small"
                        color={
                            row?.stock_upload_status
                                ? "success"
                                : "default"
                        }
                    />
                ),
            },
            {
                key: "dispatched",
                label: "Dispatched",
                render: (row) => (
                    <Chip
                        label={
                            row?.dispatched
                                ? "Yes"
                                : "No"
                        }
                        size="small"
                        color={
                            row?.dispatched
                                ? "warning"
                                : "success"
                        }
                    />
                ),
            },
            {
                key: "bootstrap_activation_status",
                label: "Bootstrap Status",
                render: (row) => (
                    <Chip
                        label={
                            row?.bootstrap_activation_status ||
                            "-"
                        }
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                key: "safe_custody_status",
                label: "Safe Custody",
                render: (row) => (
                    <Chip
                        label={
                            row?.safe_custody_status ||
                            "none"
                        }
                        size="small"
                        color={getStatusColor(
                            row?.safe_custody_status
                        )}
                    />
                ),
            },
        ],
        []
    );


    const managerColumns = useMemo(
        () => [
            {
                key: "imei",
                label: "IMEI",
                render: (row) => row?.imei || "-",
            },
            {
                key: "holder_role",
                label: "Holder Role",
                render: (row) => (
                    <Chip
                        label={
                            row?.holder_role || "-"
                        }
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                key: "holder_name",
                label: "Holder",
                render: (row) =>
                    row?.holder_name || "-",
            },
            {
                key: "previous_state",
                label: "Previous State",
                render: (row) =>
                    row?.previous_state || "-",
            },
            {
                key: "status",
                label: "Status",
                render: (row) => (
                    <Chip
                        label={
                            row?.status || "-"
                        }
                        size="small"
                        color={getStatusColor(
                            row?.status
                        )}
                    />
                ),
            },
            {
                key: "requested",
                label: "Requested At",
                render: (row) =>
                    formatDateTime(
                        row?.sc_requested_at
                    ),
            },
            {
                key: "approved",
                label: "Approved At",
                render: (row) =>
                    formatDateTime(
                        row?.sc_approved_at
                    ),
            },
            {
                key: "duration",
                label: "Duration",
                render: (row) =>
                    formatDuration(
                        row?.duration_seconds
                    ),
            },
        ],
        []
    );


    const columns = isEsimManager
        ? managerColumns
        : partyColumns;


    /* =========================================================
       Render
    ========================================================= */

    return (
        <Box
            sx={{
                p: 3,
                backgroundColor: "#f7f8fa",
                minHeight: "100vh",
            }}
        >
            {/* Header */}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        fontWeight={700}
                    >
                        Safe Custody
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Manage devices placed in and
                        returned from Safe Custody.
                    </Typography>
                </Box>

                <Tooltip title="Refresh">
                    <IconButton
                        onClick={() =>
                            isEsimManager
                                ? loadManagerData()
                                : loadPartyData()
                        }
                        disabled={loading}
                    >
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </Box>


            {/* Tabs */}

            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    mb: 2,
                }}
            >
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    variant="scrollable"
                >
                    {!isEsimManager
                        ? [
                            <Tab
                                key="eligible-devices"
                                icon={<Inventory2 />}
                                iconPosition="start"
                                label="Eligible Devices"
                            />,
                            <Tab
                                key="safe-custody"
                                icon={<Lock />}
                                iconPosition="start"
                                label="In Safe Custody"
                            />,
                        ]
                        : [
                            <Tab
                                key="pending-safe-custody"
                                label="Pending Safe Custody"
                            />,
                            <Tab
                                key="return-pending"
                                label="Return Pending"
                            />,
                            <Tab
                                key="all-requests"
                                label="All Requests"
                            />,
                            <Tab
                                key="history"
                                label="History"
                            />,
                        ]}
                </Tabs>
            </Paper>


            {/* Search + Actions */}

            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    <TextField
                        size="small"
                        value={search}
                        onChange={(event) => {
                            setSearch(
                                event.target.value
                            );
                            setPage(1);
                        }}
                        placeholder="Search IMEI"
                        sx={{
                            minWidth: 300,
                        }}
                        InputProps={{
                            startAdornment: (
                                <Search
                                    sx={{
                                        mr: 1,
                                        color: "text.secondary",
                                    }}
                                />
                            ),
                        }}
                    />

                    <Box
                        sx={{
                            flex: 1,
                        }}
                    />

                    {!isEsimManager && tab === 0 && (
                        <Button
                            variant="contained"
                            startIcon={<AddToQueue />}
                            disabled={
                                !selectedIds.length ||
                                loading
                            }
                            onClick={() =>
                                openReasonDialog(
                                    "safe-custody"
                                )
                            }
                        >
                            Send to Safe Custody
                        </Button>
                    )}

                    {!isEsimManager && tab === 1 && (
                        <Button
                            variant="contained"
                            startIcon={<Replay />}
                            disabled={
                                !selectedIds.length ||
                                loading
                            }
                            onClick={() =>
                                openReasonDialog(
                                    "return"
                                )
                            }
                        >
                            Request Return
                        </Button>
                    )}

                    {isEsimManager &&
                        tab === 0 && (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={
                                    <CheckCircle />
                                }
                                disabled={
                                    !selectedIds.length ||
                                    loading
                                }
                                onClick={() =>
                                    managerAction(
                                        "approve"
                                    )
                                }
                            >
                                Approve Safe Custody
                            </Button>
                        )}

                    {isEsimManager &&
                        tab === 1 && (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={
                                    <CheckCircle />
                                }
                                disabled={
                                    !selectedIds.length ||
                                    loading
                                }
                                onClick={() =>
                                    managerAction(
                                        "return"
                                    )
                                }
                            >
                                Approve Return
                            </Button>
                        )}
                </Box>
            </Paper>


            {/* Table */}

            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        overflowX: "auto",
                    }}
                >
                    <table
                        style={{
                            width: "100%",
                            borderCollapse:
                                "collapse",
                        }}
                    >
                        <thead>
                            <tr
                                style={{
                                    backgroundColor:
                                        "#f8fafc",
                                }}
                            >
                                <th
                                    style={{
                                        padding: "14px",
                                        textAlign:
                                            "left",
                                        borderBottom:
                                            "1px solid #e5e7eb",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={
                                            allSelected
                                        }
                                        onChange={
                                            handleSelectAll
                                        }
                                    />
                                </th>

                                {columns.map(
                                    (column) => (
                                        <th
                                            key={
                                                column.key
                                            }
                                            style={{
                                                padding:
                                                    "14px",
                                                textAlign:
                                                    "left",
                                                fontSize:
                                                    "13px",
                                                fontWeight:
                                                    600,
                                                borderBottom:
                                                    "1px solid #e5e7eb",
                                                whiteSpace:
                                                    "nowrap",
                                            }}
                                        >
                                            {
                                                column.label
                                            }
                                        </th>
                                    )
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan={
                                            columns.length +
                                            1
                                        }
                                        style={{
                                            padding:
                                                "50px",
                                            textAlign:
                                                "center",
                                        }}
                                    >
                                        <CircularProgress
                                            size={28}
                                        />
                                    </td>
                                </tr>
                            ) : rows.length ===
                                0 ? (
                                <tr>
                                    <td
                                        colSpan={
                                            columns.length +
                                            1
                                        }
                                        style={{
                                            padding:
                                                "50px",
                                            textAlign:
                                                "center",
                                            color:
                                                "#6b7280",
                                        }}
                                    >
                                        No data found
                                    </td>
                                </tr>
                            ) : (
                                rows.map(
                                    (row) => {
                                        const id =
                                            getRowId(
                                                row
                                            );

                                        return (
                                            <tr
                                                key={
                                                    id
                                                }
                                                style={{
                                                    borderBottom:
                                                        "1px solid #e5e7eb",
                                                }}
                                            >
                                                <td
                                                    style={{
                                                        padding:
                                                            "14px",
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(
                                                            id
                                                        )}
                                                        onChange={() =>
                                                            handleSelectRow(
                                                                id
                                                            )
                                                        }
                                                    />
                                                </td>

                                                {columns.map(
                                                    (
                                                        column
                                                    ) => (
                                                        <td
                                                            key={
                                                                column.key
                                                            }
                                                            style={{
                                                                padding:
                                                                    "14px",
                                                                fontSize:
                                                                    "13px",
                                                                whiteSpace:
                                                                    "nowrap",
                                                            }}
                                                        >
                                                            {column.render(
                                                                row
                                                            )}
                                                        </td>
                                                    )
                                                )}
                                            </tr>
                                        );
                                    }
                                )
                            )}
                        </tbody>
                    </table>
                </Box>


                {/* Pagination */}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent:
                            "space-between",
                        alignItems: "center",
                        p: 2,
                        borderTop:
                            "1px solid #e5e7eb",
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        {total === 0
                            ? "0 records"
                            : `Showing ${(page - 1) *
                            pageSize +
                            1
                            } - ${Math.min(
                                page *
                                pageSize,
                                total
                            )} of ${total}`}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems:
                                "center",
                            gap: 1,
                        }}
                    >
                        <IconButton
                            onClick={
                                handlePrevious
                            }
                            disabled={
                                page <= 1 ||
                                loading
                            }
                        >
                            <KeyboardArrowLeft />
                        </IconButton>

                        <Typography
                            variant="body2"
                        >
                            Page {page} of{" "}
                            {totalPages}
                        </Typography>

                        <IconButton
                            onClick={
                                handleNext
                            }
                            disabled={
                                page >=
                                totalPages ||
                                loading
                            }
                        >
                            <KeyboardArrowRight />
                        </IconButton>
                    </Box>
                </Box>
            </Paper>


            {/* Reason Dialog */}

            <Dialog
                open={reasonDialog}
                onClose={closeReasonDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {actionType ===
                        "safe-custody"
                        ? "Send to Safe Custody"
                        : "Request Return to Normal"}

                    <IconButton
                        onClick={
                            closeReasonDialog
                        }
                        sx={{
                            position:
                                "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {selectedIds.length} device
                        {selectedIds.length > 1
                            ? "s"
                            : ""}{" "}
                        selected.
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Reason"
                        value={reason}
                        onChange={(event) =>
                            setReason(
                                event.target.value
                            )
                        }
                        placeholder="Enter reason..."
                    />
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={
                            closeReasonDialog
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={
                            submitPartyAction
                        }
                        disabled={loading}
                    >
                        {loading
                            ? "Processing..."
                            : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Snackbar */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={closeSnackbar}
                    severity={
                        snackbar.severity
                    }
                    variant="filled"
                    sx={{
                        width: "100%",
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SafeCustody;
